import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import {
  AdminUserSummary,
  MemberPaymentDetails,
  apiAdminGetUserPayments,
  apiAdminListUsers,
  apiAdminUpdateUserStatus,
  ApiErrorResponse,
} from '../api/client';
import PortalPageShell from './PortalPageShell';
import * as XLSX from 'xlsx';
import { HamburgerIcon, ViewIcon } from '@chakra-ui/icons';

function isUserActive(user: AdminUserSummary) {
  return user.is_active === true || user.is_active === 1;
}

export default function AdminSubscribers() {
  const toast = useToast();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [selectedUserPayments, setSelectedUserPayments] = useState<MemberPaymentDetails[]>([]);
  const [selectedUserPaymentsLoading, setSelectedUserPaymentsLoading] = useState(false);
  const [selectedUserPaymentsError, setSelectedUserPaymentsError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [isUserPaymentsModalOpen, setIsUserPaymentsModalOpen] = useState(false);

  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  const exportUsersToExcel = () => {
    if (users.length === 0) {
      toast({
        title: 'No subscribers to export',
        description: 'There are no subscriber records available for export right now.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const rows = users.map(user => ({
      ID: user.id,
      'Full Name': user.full_name?.trim() || '',
      Email: user.email || '',
      Phone: user.phone || '',
      Role: user.role || '',
      Status: isUserActive(user) ? 'Active' : 'Disabled',
      'Is Active': isUserActive(user) ? 1 : 0,
      'Joined On': formatDate(user.joined_at || user.created_at),
      'Subscription Start': formatDate(user.subscription_started_at),
      'Subscription End': formatDate(user.subscription_expires_at),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Subscribers');

    const today = new Date();
    const dateStamp = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    XLSX.writeFile(workbook, `subscribers-${dateStamp}.xlsx`);

    toast({
      title: 'Export complete',
      description: 'The subscriber list has been downloaded as an Excel file.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const data: AdminUserSummary[] | ApiErrorResponse = await apiAdminListUsers();

    if (Array.isArray(data)) {
      setUsers(data);
      setError('');
    } else {
      setUsers([]);
      setError(data?.message || 'Unable to load subscribers right now.');
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) {
      return users;
    }

    return users.filter(user => {
      const fullName = user.full_name?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      const phone = user.phone?.toLowerCase() || '';
      return fullName.includes(term) || email.includes(term) || phone.includes(term);
    });
  }, [search, users]);

  const formatDate = (value?: string | null) => {
    if (!value) return '-';

    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  const handleToggleStatus = async (user: AdminUserSummary) => {
    const nextIsActive = !isUserActive(user);
    setUpdatingUserId(user.id);

    try {
      const result: any = await apiAdminUpdateUserStatus(user.id, nextIsActive);

      if (result?.success) {
        setUsers(currentUsers =>
          currentUsers.map(item =>
            item.id === user.id
              ? {
                  ...item,
                  is_active: nextIsActive ? 1 : 0,
                }
              : item
          )
        );

        toast({
          title: nextIsActive ? 'Subscriber enabled' : 'Subscriber disabled',
          description: result?.message || 'User status updated successfully.',
          status: 'success',
          duration: 3500,
          isClosable: true,
        });
        return;
      }

      toast({
        title: 'Update failed',
        description: result?.message || 'Unable to update user status right now.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const loadSelectedUserPayments = async (userId: number, userName: string) => {
    setSelectedUserPaymentsLoading(true);
    setSelectedUserPaymentsError('');
    setSelectedUserId(userId);
    setSelectedUserName(userName);

    try {
      const result = await apiAdminGetUserPayments(userId);
      if (Array.isArray(result)) {
        setSelectedUserPayments(result);
      } else {
        setSelectedUserPayments([]);
        setSelectedUserPaymentsError(result?.message || 'Unable to load payments for this user right now.');
      }
    } catch (error: any) {
      setSelectedUserPayments([]);
      setSelectedUserPaymentsError(error?.message || 'Unable to load payments for this user right now.');
    } finally {
      setSelectedUserPaymentsLoading(false);
      setIsUserPaymentsModalOpen(true);
    }
  };

  const closeUserPaymentsModal = () => {
    setIsUserPaymentsModalOpen(false);
    setSelectedUserId(null);
    setSelectedUserName('');
    setSelectedUserPayments([]);
    setSelectedUserPaymentsError('');
  };

  if (loading) {
    return (
      <PortalPageShell title="Subscribers" subtitle="View subscriber accounts and manage access.">
        <Stack align="center" py={12} spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading subscribers...</Text>
        </Stack>
      </PortalPageShell>
    );
  }

  return (
    <PortalPageShell
      title="Subscribers"
      subtitle="View subscriber accounts and enable or disable access."
      actions={
        <HStack spacing={3} flexWrap="wrap" justify="flex-end">
          <HStack spacing={2}>
            <IconButton
              aria-label="Card view"
              icon={<ViewIcon />}
              variant={viewMode === 'cards' ? 'solid' : 'outline'}
              colorScheme="blue"
              onClick={() => setViewMode('cards')}
            />
            <IconButton
              aria-label="Table view"
              icon={<HamburgerIcon />}
              variant={viewMode === 'table' ? 'solid' : 'outline'}
              colorScheme="blue"
              onClick={() => setViewMode('table')}
            />
          </HStack>
          <Button colorScheme="blue" onClick={exportUsersToExcel} isDisabled={users.length === 0}>
            Export Excel
          </Button>
        </HStack>
      }
    >
      <Stack spacing={5}>
        {error ? (
          <Alert status="warning" borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
        ) : null}

        <Input
          placeholder="Search by name, email, or phone"
          value={search}
          onChange={e => setSearch(e.target.value)}
          maxW="420px"
        />

        {viewMode === 'cards' ? (
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
            {filteredUsers.map(user => {
              const active = isUserActive(user);
              const displayName = user.full_name?.trim() || user.email || 'Unknown user';
              const joinedOn = user.joined_at || user.created_at;

              return (
                <Stack key={user.id} p={5} borderWidth="1px" borderColor="gray.200" borderRadius="xl" spacing={4}>
                  <HStack justify="space-between" align="flex-start" flexWrap="wrap">
                    <Stack spacing={1}>
                      <Text fontWeight="700" fontSize="lg" color="gray.800">
                        {displayName}
                      </Text>
                      <Text color="gray.600">{user.email || '-'}</Text>
                    </Stack>
                    <HStack spacing={2} flexWrap="wrap">
                      <Badge colorScheme={active ? 'green' : 'red'}>{active ? 'Active' : 'Disabled'}</Badge>
                      {user.role ? <Badge colorScheme={user.role === 'admin' ? 'purple' : 'blue'}>{user.role}</Badge> : null}
                    </HStack>
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <Text><strong>Phone:</strong> {user.phone || '-'}</Text>
                    <Text><strong>Joined:</strong> {formatDate(joinedOn)}</Text>
                    <Text><strong>Subscription Start:</strong> {formatDate(user.subscription_started_at)}</Text>
                    <Text><strong>Subscription End:</strong> {formatDate(user.subscription_expires_at)}</Text>
                  </SimpleGrid>

                  <HStack spacing={3} pt={2} flexWrap="wrap">
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => loadSelectedUserPayments(user.id, displayName)}
                    >
                      Payment details
                    </Button>
                    <Button
                      size="sm"
                      colorScheme={active ? 'red' : 'green'}
                      variant="outline"
                      onClick={() => handleToggleStatus(user)}
                      isLoading={updatingUserId === user.id}
                      loadingText={active ? 'Disabling' : 'Enabling'}
                      isDisabled={updatingUserId !== null && updatingUserId !== user.id}
                    >
                      {active ? 'Disable Login' : 'Enable Login'}
                    </Button>
                  </HStack>
                </Stack>
              );
            })}
          </SimpleGrid>
        ) : (
          <TableContainer borderWidth="1px" borderColor="gray.200" borderRadius="xl">
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th>Status</Th>
                  <Th>Role</Th>
                  <Th>Joined</Th>
                  <Th>Subscription End</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredUsers.map(user => {
                  const active = isUserActive(user);
                  const displayName = user.full_name?.trim() || user.email || 'Unknown user';
                  const joinedOn = user.joined_at || user.created_at;

                  return (
                    <Tr key={user.id}>
                      <Td fontWeight="600">{displayName}</Td>
                      <Td>{user.email || '-'}</Td>
                      <Td>{user.phone || '-'}</Td>
                      <Td>
                        <Badge colorScheme={active ? 'green' : 'red'}>{active ? 'Active' : 'Disabled'}</Badge>
                      </Td>
                      <Td>{user.role ? <Badge colorScheme={user.role === 'admin' ? 'purple' : 'blue'}>{user.role}</Badge> : '-'}</Td>
                      <Td>{formatDate(joinedOn)}</Td>
                      <Td>{formatDate(user.subscription_expires_at)}</Td>
                      <Td>
                        <HStack spacing={2} flexWrap="wrap">
                          <Button
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => loadSelectedUserPayments(user.id, displayName)}
                          >
                            Payment details
                          </Button>
                          <Button
                            size="sm"
                            colorScheme={active ? 'red' : 'green'}
                            variant="outline"
                            onClick={() => handleToggleStatus(user)}
                            isLoading={updatingUserId === user.id}
                            loadingText={active ? 'Disabling' : 'Enabling'}
                            isDisabled={updatingUserId !== null && updatingUserId !== user.id}
                          >
                            {active ? 'Disable' : 'Enable'}
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}

        {!error && filteredUsers.length === 0 ? <Text color="gray.600">No subscribers match your search.</Text> : null}
      </Stack>

      <Modal isOpen={isUserPaymentsModalOpen} onClose={closeUserPaymentsModal} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent>
          <ModalHeader>Payment details - {selectedUserName || 'User'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              {selectedUserPaymentsLoading ? (
                <Stack align="center" py={4} spacing={2}>
                  <Spinner />
                  <Text>Loading payments...</Text>
                </Stack>
              ) : selectedUserPaymentsError ? (
                <Alert status="warning" borderRadius="lg">
                  <AlertIcon />
                  {selectedUserPaymentsError}
                </Alert>
              ) : selectedUserPayments.length === 0 ? (
                <Text color="gray.600">No payment records found for this user.</Text>
              ) : (
                <Stack spacing={3}>
                  {selectedUserPayments.map(payment => (
                    <Box key={payment.id} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                        <Text><strong>Reference:</strong> {payment.payment_reference || '-'}</Text>
                        <Text><strong>Amount:</strong> {payment.currency} {Number(payment.amount).toFixed(2)}</Text>
                        <Text><strong>Status:</strong> {payment.status || '-'}</Text>
                        <Text><strong>Gateway:</strong> {payment.gateway || '-'}</Text>
                        <Text><strong>Paid At:</strong> {formatDate(payment.paid_at)}</Text>
                        <Text><strong>Subscription Start:</strong> {formatDate(payment.sub_starts_at)}</Text>
                        <Text><strong>Subscription Expires:</strong> {formatDate(payment.sub_expires_at)}</Text>
                        <Text><strong>Sub Status:</strong> {payment.sub_status || '-'}</Text>
                        <Text><strong>Stripe Subscription ID:</strong> {payment.stripe_subscription_id || '-'}</Text>
                        <Text><strong>Plan Name:</strong> {payment.plan_name || '-'}</Text>
                        <Text><strong>Plan Description:</strong> {payment.plan_description || '-'}</Text>
                        <Text><strong>Billing Cycle:</strong> {payment.billing_cycle || '-'}</Text>
                        <Text><strong>Plan Duration:</strong> {payment.plan_duration_days ?? '-'} days</Text>
                        <Text><strong>Plan Amount:</strong> {payment.plan_currency || '-'} {payment.plan_amount ?? '-'}</Text>
                      </SimpleGrid>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={closeUserPaymentsModal}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PortalPageShell>
  );
}